import { Ban, History, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import type { Column } from "@/components/shared/data-table";
import { DataTable } from "@/components/shared/data-table";
import { DeleteReasonDialog } from "@/components/shared/delete-reason-dialog";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useHasPermission, useMe } from "@/hooks/use-auth";
import { useDeleteSale, useVoidSale } from "@/hooks/use-sales";
import { useServerTable } from "@/hooks/use-server-table";
import { useWorkspaceFilterOption } from "@/hooks/use-workspace-filter";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { queryKeys } from "@/lib/query-keys";
import { EntityActivityLogDialog } from "@/pages/activity-logs/entity-activity-log-dialog";
import { saleService } from "@/services/sale";

import { SaleDialog } from "./sale-dialog";
import {
  SALE_CHANNEL_LABELS,
  SALE_PAYMENT_METHOD_LABELS,
  SALE_PAYMENT_STATUS_LABELS,
} from "./sale-labels";

import type { Sale } from "@artisancode/api-types";

export function SaleList() {
  const canCreate = useHasPermission("sales.create");
  const canUpdate = useHasPermission("sales.update");
  const canDelete = useHasPermission("sales.delete");
  const canVoid = useHasPermission("sales.void");
  const canViewLogs = useHasPermission("sales.view");

  const { mutate: deleteSale, isPending: isDeleting } = useDeleteSale();
  const { mutate: voidSale, isPending: isVoiding } = useVoidSale();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Sale | null>(null);
  const [deleting, setDeleting] = useState<Sale | null>(null);
  const [voiding, setVoiding] = useState<Sale | null>(null);
  const [historySaleId, setHistorySaleId] = useState<string | null>(null);

  const { data: me } = useMe();
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = workspacesData?.items ?? [];

  function workspaceName(workspaceId: string) {
    return workspaces.find((w) => w.id === workspaceId)?.name ?? "-";
  }

  const workspaceFilter = useWorkspaceFilterOption();
  const table = useServerTable<Sale>({
    queryKey: (params) => queryKeys.sales.list(params),
    fetcher: (params) => saleService.list(params),
    pageSize: 10,
    filterKeys: ["date_from", "date_to", "workspace_id"],
  });

  function handleDeleteConfirm(reason: string) {
    if (!deleting) return;
    deleteSale(
      { id: deleting.id, reason },
      {
        onSuccess: () => {
          toast.success("Penjualan berhasil dihapus.");
          setDeleting(null);
        },
        onError: () => toast.error("Gagal menghapus penjualan."),
      },
    );
  }

  function handleVoidConfirm(reason: string) {
    if (!voiding) return;
    voidSale(
      { id: voiding.id, reason },
      {
        onSuccess: () => {
          toast.success("Penjualan berhasil dibatalkan (void).");
          setVoiding(null);
        },
        onError: () => toast.error("Gagal membatalkan penjualan."),
      },
    );
  }

  const columns: Column<Sale>[] = [
    {
      key: "date",
      label: "Tanggal & No. ID",
      render: (s) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-medium">{s.date}</span>
            {s.transactionCode && (
              <Badge
                variant="outline"
                className="font-mono text-[10px] px-1.5 py-0 bg-muted/40 text-foreground"
              >
                {s.transactionCode}
              </Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            {SALE_CHANNEL_LABELS[s.channel]}
          </span>
        </div>
      ),
    },
    {
      key: "customer",
      label: "Pelanggan / Produk",
      render: (s) => (
        <div className="flex flex-col">
          <span className="font-medium">{s.customerName || "-"}</span>
          <span className="text-xs text-muted-foreground">
            {s.items[0]?.productName ?? "-"}
            {s.items.length > 1 ? ` +${s.items.length - 1} lainnya` : ""}
          </span>
        </div>
      ),
    },
    ...(me?.canSwitchWorkspace
      ? [
          {
            key: "workspace",
            label: "Workspace",
            render: (s: Sale) => (
              <span className="text-sm">{workspaceName(s.workspaceId)}</span>
            ),
          },
        ]
      : []),
    {
      key: "total",
      label: "Total",
      render: (s) => (
        <div className="flex flex-col">
          <span>{(s.total ?? 0).toLocaleString("id-ID")}</span>
          {s.validation === "MISSING_COGS" && (
            <span className="text-xs text-destructive">COGS belum diisi</span>
          )}
        </div>
      ),
    },
    {
      key: "payment",
      label: "Pembayaran",
      render: (s) => (
        <div className="flex flex-col gap-1">
          <Badge
            variant={
              s.paymentStatus === "paid"
                ? "default"
                : s.paymentStatus === "cancelled"
                  ? "destructive"
                  : "outline"
            }
          >
            {SALE_PAYMENT_STATUS_LABELS[s.paymentStatus]}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {SALE_PAYMENT_METHOD_LABELS[s.paymentMethod]}
          </span>
          {s.paymentStatus === "cancelled" && s.cancelReason && (
            <span className="text-xs text-muted-foreground italic">
              &ldquo;{s.cancelReason}&rdquo;
            </span>
          )}
        </div>
      ),
    },
  ];

  function isEditable(s: Sale) {
    return s.paymentStatus !== "paid" && s.paymentStatus !== "cancelled";
  }

  function openAdd() {
    setEditing(null);
    setOpen(true);
  }

  function openEdit(item: Sale) {
    setEditing(item);
    setOpen(true);
  }

  return (
    <div>
      <PageHeader
        title="Penjualan"
        description="Input penjualan kopi & roti non-rider."
        action={
          <Button size="sm" onClick={openAdd} disabled={!canCreate}>
            <Plus className="mr-1 h-4 w-4" />
            Tambah
          </Button>
        }
      />
      <DataTable
        data={table.items}
        loadedData={table.loadedItems}
        columns={columns}
        searchPlaceholder="Cari pelanggan / ID transaksi..."
        query={table.query}
        onQueryChange={table.onQueryChange}
        filters={workspaceFilter}
        activeFilters={table.activeFilters}
        onFilterChange={table.onFilterChange}
        dateRange={{
          from: table.activeFilters.date_from ?? "",
          to: table.activeFilters.date_to ?? "",
          onChange: (date_from, date_to) =>
            table.onFiltersChange({ date_from, date_to }),
        }}
        page={table.page}
        totalPages={table.totalPages}
        totalCount={table.totalCount}
        onPageChange={table.onPageChange}
        hasMore={table.hasMore}
        onLoadMore={table.onLoadMore}
        loading={table.loading}
        actions={(item) => (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openEdit(item)}
              disabled={!canUpdate || !isEditable(item)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            {item.paymentStatus === "paid" ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setVoiding(item)}
                disabled={!canVoid}
                title="Void / batalkan transaksi"
              >
                <Ban className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDeleting(item)}
                disabled={!canDelete || !isEditable(item)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            {canViewLogs && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setHistorySaleId(item.id)}
                title="Riwayat Aktivitas"
                aria-label="Riwayat Aktivitas"
              >
                <History className="h-4 w-4" />
                <span className="sr-only">Riwayat Aktivitas</span>
              </Button>
            )}
          </div>
        )}
        mobileRender={(s) => (
          <Card className="overflow-hidden border border-border shadow-xs">
            <div className="p-3.5 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold text-sm">{s.date}</span>
                  {s.transactionCode && (
                    <Badge
                      variant="outline"
                      className="font-mono text-[10px] px-1.5 py-0 bg-muted/40 text-foreground"
                    >
                      {s.transactionCode}
                    </Badge>
                  )}
                </div>
                <Badge
                  variant={s.paymentStatus === "paid" ? "default" : "outline"}
                  className="text-[11px] px-2 py-0.5"
                >
                  {SALE_PAYMENT_STATUS_LABELS[s.paymentStatus]}
                </Badge>
              </div>

              <div className="flex items-baseline justify-between gap-2">
                <div>
                  <p className="font-medium text-sm text-foreground">
                    {s.customerName || "Pelanggan Umum"}
                  </p>
                  <span className="text-xs text-muted-foreground">
                    Channel: {SALE_CHANNEL_LABELS[s.channel]}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs text-muted-foreground block">
                    Metode
                  </span>
                  <span className="text-xs font-medium">
                    {SALE_PAYMENT_METHOD_LABELS[s.paymentMethod]}
                  </span>
                </div>
              </div>

              <div className="rounded-md bg-muted/40 p-2.5 text-xs space-y-1">
                {s.items.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between items-center text-muted-foreground"
                  >
                    <span className="truncate pr-2">{item.productName}</span>
                    <span className="shrink-0 font-mono">
                      {item.qty}x · Rp{" "}
                      {(
                        (item.sellingPrice ?? 0) * (item.qty ?? 1)
                      ).toLocaleString("id-ID")}
                    </span>
                  </div>
                ))}
                {s.validation === "MISSING_COGS" && (
                  <p className="text-[11px] text-destructive font-medium pt-1 border-t border-border/50">
                    ⚠️ COGS belum diisi
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between pt-1 border-t border-border/40 flex-wrap gap-2">
                <div>
                  <span className="text-[11px] text-muted-foreground block">
                    Total Transaksi
                  </span>
                  <span className="text-base font-bold text-foreground">
                    Rp {(s.total ?? 0).toLocaleString("id-ID")}
                  </span>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {canViewLogs && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 text-xs"
                      onClick={() => setHistorySaleId(s.id)}
                      title="Riwayat Aktivitas"
                      aria-label="Riwayat Aktivitas"
                    >
                      <History className="mr-1 h-3.5 w-3.5" />
                      Riwayat Aktivitas
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2.5 text-xs"
                    onClick={() => openEdit(s)}
                    disabled={!canUpdate || !isEditable(s)}
                  >
                    <Pencil className="mr-1 h-3.5 w-3.5" />
                    Edit
                  </Button>
                  {s.paymentStatus === "paid" ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => setVoiding(s)}
                      disabled={!canVoid}
                    >
                      <Ban className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => setDeleting(s)}
                      disabled={!canDelete || !isEditable(s)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}
      />

      <SaleDialog open={open} onOpenChange={setOpen} editing={editing} />
      <EntityActivityLogDialog
        entityName="sale"
        entityId={historySaleId}
        open={historySaleId !== null}
        onOpenChange={(next) => !next && setHistorySaleId(null)}
      />
      <DeleteReasonDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && setDeleting(null)}
        title="Hapus Penjualan"
        description={
          deleting
            ? `Transaksi "${deleting.items[0]?.productName ?? "-"}" (${deleting.customerName || "-"}) akan dihapus.`
            : undefined
        }
        onConfirm={handleDeleteConfirm}
        loading={isDeleting}
      />
      <DeleteReasonDialog
        open={voiding !== null}
        onOpenChange={(next) => !next && setVoiding(null)}
        title="Void / Batalkan Transaksi"
        description={
          voiding
            ? `Transaksi "${voiding.items[0]?.productName ?? "-"}" (${voiding.customerName || "-"}) akan dibatalkan. Transaksi tetap tercatat dengan status Batal.`
            : undefined
        }
        onConfirm={handleVoidConfirm}
        loading={isVoiding}
        fieldLabel="Alasan void"
        placeholder="Jelaskan alasan pembatalan transaksi..."
        confirmLabel="Void"
      />
    </div>
  );
}
